import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiEye, FiFilter, FiX, 
  FiThumbsUp, FiBookOpen, FiAward, FiAlertTriangle
} from 'react-icons/fi';
import lessonLearnedService from '../../services/lessonLearnedService';
import projectService from '../../services/projectService';
import CommonModal from '../../services/CommonModal';

const LessonsLearned = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [lessonsLearned, setLessonsLearned] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
    const confirmDelete = () => {
    handleDelete(selectedLesson.id);
    setShowDeleteModal(false);
    setSelectedLesson(null);
  };
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    project_id: searchParams.get('project_id') || '',
    category: searchParams.get('category') || '',
    type: searchParams.get('type') || '',
    phase: searchParams.get('phase') || '',
    impact: searchParams.get('impact') || '',
    applicability: searchParams.get('applicability') || '',
    status: searchParams.get('status') || '',
    is_shared: searchParams.get('is_shared') || ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const categories = [
    'Technical', 'Process', 'Communication', 'Resource Management',
    'Risk Management', 'Quality', 'Stakeholder Management', 'Budget',
    'Schedule', 'Team Dynamics', 'Tools & Technology', 'Other'
  ];

  const types = ['Success', 'Challenge', 'Best Practice', 'Mistake', 'Improvement'];
  const phases = ['Initiation', 'Planning', 'Execution', 'Monitoring & Control', 'Closure', 'Post-Project'];
  const impacts = ['High', 'Medium', 'Low'];
  const applicabilities = ['Project-Specific', 'Department-Wide', 'Organization-Wide', 'Industry-Wide'];
  const statuses = ['Draft', 'Under Review', 'Approved', 'Archived'];

  useEffect(() => {
    fetchLessonsLearned();
    fetchProjects();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchLessonsLearned = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const data = await lessonLearnedService.getLessonsLearned(params);
      setLessonsLearned(data.lessonsLearned || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch lessons learned');
      console.error('Error fetching lessons learned:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const data = await lessonLearnedService.getLessonsLearnedStats(params);
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      project_id: '',
      category: '',
      type: '',
      phase: '',
      impact: '',
      applicability: '',
      status: '',
      is_shared: ''
    });
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {

    try {
      await lessonLearnedService.deleteLessonLearned(id);
      toast.success('Lesson learned deleted successfully');
      fetchLessonsLearned();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete lesson learned');
      console.error('Error deleting lesson learned:', error);
    }
  };

  const handleMarkHelpful = async (id) => {
    try {
      await lessonLearnedService.markAsHelpful(id);
      toast.success('Marked as helpful');
      fetchLessonsLearned();
    } catch (error) {
      toast.error('Failed to mark as helpful');
      console.error('Error marking as helpful:', error);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'Success': 'bg-green-100 text-green-800',
      'Challenge': 'bg-yellow-100 text-yellow-800',
      'Best Practice': 'bg-blue-100 text-blue-800',
      'Mistake': 'bg-red-100 text-red-800',
      'Improvement': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getImpactColor = (impact) => {
    const colors = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return colors[impact] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Under Review': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Archived': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && lessonsLearned.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading lessons learned...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lessons Learned</h1>
          <p className="text-gray-600 mt-1">Capture and share project insights</p>
        </div>
        <Link
          to="/app/lessons-learned/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Add Lesson Learned
        </Link>
      </div>
                         <CommonModal
        isOpen={showDeleteModal}
        title="Delete Lesson Learned"
        message="Are you sure you want to delete this lesson learned?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedLesson(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Lessons</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Approved</div>
            <div className="text-3xl font-bold text-green-600 mt-2">
              {stats.byStatus?.['Approved'] || 0}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Shared</div>
            <div className="text-3xl font-bold text-blue-600 mt-2">{stats.sharedCount || 0}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">High Impact</div>
            <div className="text-3xl font-bold text-red-600 mt-2">
              {stats.byImpact?.['High'] || 0}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <FiFilter /> {showFilters ? 'Hide' : 'Show'} Filters
          </button>
          {Object.values(filters).some(v => v) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <FiX /> Clear Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              name="search"
              placeholder="Search lessons..."
              value={filters.search}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            />
            <select
              name="project_id"
              value={filters.project_id}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Types</option>
              {types.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              name="phase"
              value={filters.phase}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Phases</option>
              {phases.map(phase => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </select>
            <select
              name="impact"
              value={filters.impact}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Impacts</option>
              {impacts.map(impact => (
                <option key={impact} value={impact}>{impact}</option>
              ))}
            </select>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              name="is_shared"
              value={filters.is_shared}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Lessons</option>
              <option value="true">Shared Only</option>
              <option value="false">Not Shared</option>
            </select>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Impact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lessonsLearned.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No lessons learned found
                  </td>
                </tr>
              ) : (
                lessonsLearned.map((lesson) => (
                  <tr key={lesson.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{lesson.title}</div>
                      <div className="text-sm text-gray-500">{lesson.project?.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lesson.category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(lesson.type)}`}>
                        {lesson.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getImpactColor(lesson.impact)}`}>
                        {lesson.impact}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lesson.status)}`}>
                        {lesson.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FiBookOpen /> {lesson.views_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <FiThumbsUp /> {lesson.helpful_count}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/app/lessons-learned/${lesson.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => handleMarkHelpful(lesson.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Mark as Helpful"
                        >
                          <FiThumbsUp />
                        </button>
                        <button
                          onClick={() => navigate(`/app/lessons-learned/${lesson.id}/edit`)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLesson(lesson);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                  <span className="font-medium">{pagination.pages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonsLearned;

